import React, { useEffect, useRef, useState } from "react";
import { SkipForward, Volume2, VolumeX, Radio } from "lucide-react";
import api from "@/lib/api";
import { nextLive } from "@/lib/host";

const LOGO_TRANSPARENT = "/logo-transparent.png";

function seasonTheme() {
  const d = new Date();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  if (m === 12 && day <= 24) return { glow: "#7C5CFA", ring: "rgba(124,92,250,0.35)", accent: "#B6A6FF", label: "Advent" };
  if ((m === 12 && day >= 25) || (m === 1 && day <= 6)) return { glow: "#E64C3C", ring: "rgba(230,179,74,0.4)", accent: "#E6B34A", label: "Christmas" };
  if (m === 2 || (m === 3 && day <= 25)) return { glow: "#6B4E9E", ring: "rgba(107,78,158,0.4)", accent: "#C4A6E0", label: "Lent" };
  if ((m === 3 && day > 25) || m === 4) return { glow: "#3FB98C", ring: "rgba(63,185,140,0.4)", accent: "#7EE0BE", label: "Easter" };
  return { glow: "#E6B34A", ring: "rgba(230,179,74,0.3)", accent: "#E6B34A", label: null };
}

const todayKey = () => new Date().toISOString().slice(0, 10);

const LYRICS = [
  { text: "Great is Thy faithfulness, Lord unto me.", reference: "Great Is Thy Faithfulness" },
  { text: "Blessed be the name of the Lord.", reference: "Blessed Be Your Name" },
  { text: "It is well, it is well with my soul.", reference: "It Is Well With My Soul" },
  { text: "Way maker, miracle worker, promise keeper.", reference: "Way Maker" },
  { text: "Amazing grace, how sweet the sound.", reference: "Amazing Grace" },
  { text: "Oh, how He loves us, oh, how He loves us.", reference: "How He Loves" },
  { text: "I'll sing of the goodness of God.", reference: "Goodness of God" },
];
const fmtCountdown = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return (h > 0 ? `${h}:` : "") + `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

function playChime() {
  if (localStorage.getItem("splashSound") === "off") return;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    const t0 = ctx.currentTime;
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = f;
      o.connect(g); g.connect(ctx.destination);
      const t = t0 + i * 0.16;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.1, t + 0.05);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 1.8);
      o.start(t); o.stop(t + 1.9);
    });
  } catch (e) {}
}

export default function SplashScreen() {
  const [verse, setVerse] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const [gone, setGone] = useState(() => localStorage.getItem("splashDay") === todayKey());
  const [live, setLive] = useState(false);
  const [tick, setTick] = useState(Date.now());
  const [soundOff, setSoundOff] = useState(() => localStorage.getItem("splashSound") === "off");
  const theme = seasonTheme();
  const started = useRef(false);

  useEffect(() => {
    if (gone) return;
    localStorage.setItem("splashDay", todayKey());
    if (Math.random() < 0.34) {
      const l = LYRICS[Math.floor(Math.random() * LYRICS.length)];
      setVerse({ text: l.text, reference: `♪ ${l.reference}`, isLyric: true });
    } else {
      api.get("/scripture/today").then((r) => setVerse(r.data)).catch(() => {});
    }
    api.get("/live/status").then((r) => setLive(!!r.data?.id)).catch(() => {});
    if (!started.current) { started.current = true; playChime(); }
    const tk = setInterval(() => setTick(Date.now()), 1000);
    const t1 = setTimeout(() => setLeaving(true), 7500);
    const t2 = setTimeout(() => setGone(true), 8200);
    return () => { clearInterval(tk); clearTimeout(t1); clearTimeout(t2); };
  }, [gone]);

  const dismiss = () => { setLeaving(true); setTimeout(() => setGone(true), 600); };
  const toggleSound = (e) => {
    e.stopPropagation();
    const off = !soundOff; setSoundOff(off);
    localStorage.setItem("splashSound", off ? "off" : "on");
    if (!off) playChime();
  };

  if (gone) return null;

  const next = nextLive(new Date(tick));
  const msToNext = next ? next.date - new Date(tick) : null;
  const soon = msToNext !== null && msToNext <= 30 * 60 * 1000;
  const words = verse ? `\u201c${verse.text}\u201d`.split(" ") : [];

  return (
    <div onClick={dismiss}
      className={`fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[#0A1030] transition-opacity duration-700 ${leaving ? "opacity-0" : "opacity-100"}`}
      data-testid="splash-screen">
      <div className="absolute w-[420px] h-[420px] rounded-full blur-3xl splash-glow" style={{ backgroundColor: theme.glow, opacity: 0.12 }} />

      <button onClick={toggleSound} data-testid="splash-sound-toggle" className="absolute top-6 right-6 p-2.5 rounded-full text-zinc-400 hover:text-white bg-white/5 hover:bg-white/10 transition-colors splash-fade" style={{ animationDelay: "1.2s" }}>
        {soundOff ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>

      <div className="relative flex flex-col items-center px-8 text-center">
        <div className="relative splash-logo">
          <span className="absolute inset-0 rounded-full border splash-ring" style={{ borderColor: theme.ring }} />
          <span className="absolute inset-0 rounded-full border splash-ring" style={{ borderColor: theme.ring, animationDelay: "0.6s" }} />
          <img src={LOGO_TRANSPARENT} alt="In His Image Radio" data-testid="splash-logo" className="relative w-32 h-32 lg:w-36 lg:h-36 object-contain" style={{ filter: `drop-shadow(0 0 40px ${theme.glow}66)` }} />
        </div>

        <h1 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mt-8 splash-fade" style={{ animationDelay: "0.4s" }}>In His Image Radio</h1>
        <p className="overline mt-2 splash-fade" style={{ animationDelay: "0.7s", color: theme.accent }}>Gospel · Praise · Worship</p>
        {theme.label && <p className="text-xs text-zinc-500 mt-1 splash-fade" style={{ animationDelay: "0.8s" }}>{theme.label} season</p>}

        <div className="h-px w-16 my-6 splash-fade" style={{ animationDelay: "1s", backgroundColor: `${theme.accent}66` }} />

        {live ? (
          <button onClick={(e) => { e.stopPropagation(); window.location.href = "/live"; }} className="splash-fade" data-testid="splash-live" style={{ animationDelay: "1.1s" }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF3B30]/15 border border-[#FF3B30]/40 text-[#FF3B30] font-bold">
              <span className="w-2 h-2 rounded-full bg-[#FF3B30] live-dot" /> David Porter is LIVE now
            </span>
            <p className="text-sm text-zinc-300 mt-3">Tap to tune in →</p>
          </button>
        ) : soon ? (
          <div className="splash-fade" data-testid="splash-countdown" style={{ animationDelay: "1.1s" }}>
            <p className="overline text-[#E6B34A] flex items-center justify-center gap-1.5"><Radio className="w-3.5 h-3.5" /> {next.title} starts in</p>
            <p className="font-display font-black text-4xl lg:text-5xl tracking-tighter tabular-nums mt-2" style={{ color: theme.accent }}>{fmtCountdown(msToNext)}</p>
            <p className="text-sm text-zinc-400 mt-2">David Porter is about to go live</p>
          </div>
        ) : verse ? (
          <div className="max-w-md" data-testid="splash-verse">
            <p className="font-display text-lg lg:text-xl text-zinc-100 leading-relaxed italic">
              {words.map((w, i) => (
                <React.Fragment key={i}>
                  <span className="splash-word" style={{ animationDelay: `${1.1 + i * 0.09}s` }}>{w}</span>{" "}
                </React.Fragment>
              ))}
            </p>
            <p className="font-semibold mt-3 text-sm splash-fade" style={{ animationDelay: `${1.1 + words.length * 0.09 + 0.2}s`, color: theme.accent }}>{verse.reference}</p>
          </div>
        ) : <div className="h-6" />}
      </div>

      <button onClick={(e) => { e.stopPropagation(); dismiss(); }} data-testid="splash-skip"
        className="absolute bottom-10 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-zinc-400 hover:text-white bg-white/5 hover:bg-white/10 transition-colors splash-fade" style={{ animationDelay: "1.4s" }}>
        Skip <SkipForward className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
